import {html, type PropertyValues, type TemplateResult} from "lit";
import {customElement, property, query, state} from "lit/decorators.js";
import {unsafeSVG} from "lit/directives/unsafe-svg.js";
import CLOSE_ICON from "../../../../../svgpictograms/close.svg";
import PLAY_ICON from "../../../../../svgpictograms/play.svg";
import PAUSE_ICON from "../../../../../svgpictograms/pause.svg";
import {resolve} from "../../../container";
import {intRangeClosed} from "../../../bootstrap/common/arrays";
import {type CleanableStoryTile} from "./energyBitStoryTile";
import {classMap} from "lit/directives/class-map.js";
import {eventOccurredOutside, KEYS} from "../../../common/utils/events";
import {Timeout} from "../../../common/timeout";
import {TouchControlHandler} from "../../../common/utils/touchControlHandler";
import type {Nullable} from "../../../bootstrap/common/types";
import {trackingButtonInteract} from "../../../tracking/trackingAttributes";
import {UnLitElement} from "../../../common/elements";

export const FADE_DURATION_IN_S = 0.5; //has to be in sync with CSS

@customElement("eop-energy-bit-story-viewer")
export class EopEnergyBitStoryViewer extends UnLitElement {

    @property({reflect: true, type: Boolean})
    private playing: boolean = false;
    @property({reflect: true, type: Boolean})
    private open: boolean = false;
    @state()
    private progress: number = 0;
    @query(".story-container")
    private storyContainer: HTMLElement;
    @query(".bar-container[animated] .bar.active")
    private activeProgressBar: HTMLElement;
    @query(".close-button")
    private closeButton: HTMLButtonElement;

    private images: NodeListOf<HTMLElement>;
    private managingParent: CleanableStoryTile;
    private openerElement: Nullable<HTMLElement>;

    public constructor(
        private timeout: Timeout = resolve(Timeout)
    ) {
        super();
        this.images = this.querySelectorAll(".story-image");
        this.style.setProperty("--bars-count", String(this.images.length));
    }

    public render(): TemplateResult {
        return html`
            <div class="story-container">
                ${this.renderStoryImages()}
                <div class="interaction-overlay">
                    ${this.renderProgress()}
                    ${this.renderControlElements()}
                </div>
                <button class="prev-button" @click=${this.previous} ${trackingButtonInteract("energy-bit-previous-button")}></button>
                <button class="next-button" @click=${this.nextOrClose} ${trackingButtonInteract("energy-bit-next-button")}></button>
            </div>
            <eop-tab-out-listener .callback=${() => this.closeStory()}></eop-tab-out-listener>
        `;
    }

    private renderStoryImages(): TemplateResult {
        return html`
            <div class="story-images" role="group" aria-label="Story image ${this.progress + 1} of ${this.images.length}">
                ${this.renderImages()}
            </div>`;
    }

    private renderImages(): TemplateResult[] {
        return Array.from(this.images).map((image, index) => {
            const classes = classMap({
                "active": index === this.progress
            });
            return html`
                <div class="story-image-container ${classes}">
                    ${image}
                </div>`;
        });
    }

    private renderProgress(): TemplateResult {
        return html`
            <div class="progress">
                <div class="bar-container">${this.renderProgressBars()}</div>
                <div class="bar-container" animated @animationend=${this.nextOrClose}>${this.renderProgressBars()}</div>
            </div>`;
    }

    private renderProgressBars(): TemplateResult[] {
        return intRangeClosed(1, this.images.length).map((_, index) => {
            const classes = classMap({
                "active": this.playing && index === this.progress,
                "past": index < this.progress,
                "upcoming": index > this.progress
            });
            return html`
                <div class="bar ${classes}"></div>`;
        });
    }

    private renderControlElements(): TemplateResult {
        const PLAY_PAUSE_ICON = this.playing ? PAUSE_ICON : PLAY_ICON;
        return html`
            <div class="control">
                <button class="resume-pause-button" @click=${this.togglePause} ${trackingButtonInteract("energy-bit-resume-pause-button")}>
                    ${unsafeSVG(PLAY_PAUSE_ICON)}
                </button>
                <button class="close-button" @click=${this.closeStory} ${trackingButtonInteract("energy-bit-close-button")}>
                    ${unsafeSVG(CLOSE_ICON)}
                </button>
            </div>
        `;
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        this.addEventListener("click", (event: MouseEvent) => {
            if (eventOccurredOutside(event, this.storyContainer)) {
                this.closeStory();
            }
        });
        this.addEventListener("contextmenu", (event: Event) => {
            event.preventDefault();
        });
        this.addEventListener("keydown", (event: Event) => {
            const keyEvent = event as KeyboardEvent;
            if (keyEvent.key === KEYS.ESCAPE) {
                this.closeStory();
            } else if (keyEvent.key === KEYS.ARROW_LEFT) {
                this.previous();
            } else if (keyEvent.key === KEYS.ARROW_RIGHT) {
                this.nextOrClose();
            }
        });

        new TouchControlHandler([this.storyContainer])
            .onTouchLeft(() => this.previous())
            .onTouchRight(() => this.nextOrClose())
            .onTouchLongPress(() => this.pause())
            .onTouchEnd(() => this.resume())
            .activate();
    }

    public setManagingParent(tile: CleanableStoryTile): void {
        this.managingParent = tile;
    }

    public setOpener(opener: HTMLElement): void {
        this.openerElement = opener;
    }

    public openStory(): void {
        this.open = true;
        this.progress = 0;
        this.playing = true;
        void this.updateComplete.then(() => this.closeButton?.focus());
    }

    private closeStory(): void {
        this.open = false;
        void this.timeout.delay(() => {
            this.managingParent.cleanUp();
            this.openerElement?.focus();
        }, FADE_DURATION_IN_S * 1000);
    }

    private nextOrClose(): void {
        if (this.isLastImage()) {
            this.closeStory();
        } else {
            this.next();
        }
    }

    private isLastImage(): boolean {
        return this.progress === this.images.length - 1;
    }

    private togglePause(): void {
        this.playing = !this.playing;
    }

    private resume(): void {
        this.playing = true;
    }

    private pause(): void {
        this.playing = false;
    }

    private previous(): void {
        this.progress = Math.max(0, this.progress - 1);
        this.playing = true;
        void this.timeout.delay(() => this.restartAnimation(), 0);
    }

    private next(): void {
        this.progress++;
        this.playing = true;
    }

    private restartAnimation(): void {
        //https://medium.com/@khalidoghli7/how-to-trigger-a-reflow-for-flushing-css-changes-e89c96030c04
        this.activeProgressBar.style.animation = "none";
        void this.activeProgressBar.offsetWidth;
        this.activeProgressBar.style.animation = "";
    }
}